import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth, hasRole } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { BRAND } from '@/lib/brand';

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAdmin = hasRole(user, ['admin']);
  const [maint, setMaint] = useState({ enabled: false, message: '' });

  const load = async () => {
    const { data } = await api.get('/settings/global-maintenance');
    if (data && typeof data.enabled !== 'undefined') setMaint({ enabled: !!data.enabled, message: data.message || '' });
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.put('/settings/global-maintenance', maint); toast.success(t('success')); }
    catch (e) { toast.error(t('error')); }
  };

  const switchLang = (l) => { i18n.changeLanguage(l); localStorage.setItem('lang', l); };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="font-display text-2xl font-semibold">{t('settings_title')}</h1>
      </div>

      <Card className="p-5 bg-card border border-border">
        <h2 className="font-display text-lg font-semibold mb-3">{t('settings_language')}</h2>
        <div className="flex gap-2">
          <Button variant={i18n.language === 'es' ? 'default' : 'outline'} onClick={() => switchLang('es')} data-testid="settings-lang-es">Español</Button>
          <Button variant={i18n.language === 'en' ? 'default' : 'outline'} onClick={() => switchLang('en')} data-testid="settings-lang-en">English</Button>
        </div>
      </Card>

      <Card className="p-5 bg-card border border-border">
        <h2 className="font-display text-lg font-semibold mb-3">{t('settings_global_maint')}</h2>
        <div className="flex items-center justify-between mb-4">
          <Label>Enabled</Label>
          <Switch checked={maint.enabled} onCheckedChange={(v) => setMaint({ ...maint, enabled: v })} disabled={!isAdmin} data-testid="global-maint-switch" />
        </div>
        <div className="space-y-1">
          <Label>{t('settings_global_maint_msg')}</Label>
          <Textarea value={maint.message} onChange={(e) => setMaint({ ...maint, message: e.target.value })} disabled={!isAdmin} />
        </div>
        <div className="mt-3"><Button onClick={save} disabled={!isAdmin} data-testid="global-maint-save">{t('save')}</Button></div>
      </Card>

      <Card className="p-5 bg-card border border-border">
        <h2 className="font-display text-lg font-semibold mb-3">Profile</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-[hsl(var(--muted-foreground))]">Name</span><span>{user?.name}</span></div>
          <div className="flex justify-between"><span className="text-[hsl(var(--muted-foreground))]">Email</span><span className="font-mono">{user?.email}</span></div>
          <div className="flex justify-between"><span className="text-[hsl(var(--muted-foreground))]">Role</span><span className="uppercase font-mono text-xs">{user?.role}</span></div>
        </div>
      </Card>

      <Card className="p-5 bg-card border border-border">
        <h2 className="font-display text-lg font-semibold mb-3">About</h2>
        <div className="flex items-center gap-3">
          <img src={BRAND.logoWhite} alt={BRAND.name} className="h-10 w-10 object-contain" />
          <div>
            <div className="font-display font-semibold">{BRAND.product} • {BRAND.name}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">{BRAND.tagline_es}</div>
          </div>
        </div>
      </Card>
    </div>
  );
}
